import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { listCompactSx } from "@/styles/sx/compact";
import {
  type ReassistBillingItem,
  type ReassistBillingPurchase,
  type ReassistMonthlyBilling,
} from "../types";
import { downloadBillingCsv } from "../utils/billingCsv";
import { billingTotals } from "../utils/billingTotals";
import { formatAmount, formatDay, formatMonth } from "../utils/format";
import { costItemSx, tapExpandedSx, TEXT } from "../utils/tokens";
import { AmountText } from "./AmountText";

interface Props {
  /** 明細を出す月。null のときは閉じる。 */
  billing: ReassistMonthlyBilling | null;
  /** 月の行と同じ暫定額・合計の表示（表と同じ文字にそろえる）。 */
  provisionalText: (row: ReassistMonthlyBilling) => string;
  totalText: (row: ReassistMonthlyBilling) => string;
  /** 月の行と同じ黒塗り購入の表示。 */
  purchaseText: (row: ReassistMonthlyBilling) => string;
  onClose: () => void;
}

/** 明細の1行の、左上の日付。納品済みは納品日、納品前は依頼日を添える。 */
const dateLabel = (item: ReassistBillingItem): string =>
  item.deliveredOn !== null
    ? `納品日 ${formatDay(item.deliveredOn)}`
    : `納品前（依頼日 ${formatDay(item.requestedOn)}）`;

/** 手数料（税抜）・経費（非課税）・消費税の内訳。分からない項目は出さない。 */
const breakdownText = (item: ReassistBillingItem): string =>
  [
    item.handlingFee !== null
      ? `手数料 ${formatAmount(item.handlingFee)}`
      : null,
    item.expense !== null ? `経費 ${formatAmount(item.expense)}` : null,
    item.tax !== null ? `消費税 ${formatAmount(item.tax)}` : null,
  ]
    .filter((v): v is string => v !== null)
    .join("・");

/** 明細の1行（依頼1件）。 */
const BillingItemRow: React.FC<{ item: ReassistBillingItem }> = ({ item }) => {
  const breakdown = breakdownText(item);
  return (
    <Box
      component="li"
      sx={(theme) => ({
        ...costItemSx(theme),
        flexWrap: "wrap",
        alignItems: "flex-start",
        columnGap: 2,
        rowGap: 1,
        listStyle: "none",
      })}
    >
      <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
        <Typography
          sx={{
            ...TEXT.meta,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {dateLabel(item)}
        </Typography>
        <Typography
          sx={{ ...TEXT.body, fontWeight: 700, overflowWrap: "anywhere" }}
        >
          {item.address}
        </Typography>
        {item.buildingName !== "" && (
          <Typography
            sx={{
              ...TEXT.body,
              color: "text.primary",
              overflowWrap: "anywhere",
            }}
          >
            {item.buildingName}
          </Typography>
        )}
        <Typography
          sx={{
            ...TEXT.meta,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          書類 {item.documentCount}件
        </Typography>
      </Box>
      <Box
        sx={{
          flex: "0 0 auto",
          ml: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
          textAlign: "right",
        }}
      >
        {/* 暫定だけチップで示す（確定は既定なので出さない）。金額が null なら「金額未定」 */}
        <AmountText
          amount={item.total}
          taxIncluded
          isProvisional={item.isProvisional}
          showChip={item.isProvisional}
        />
        {breakdown !== "" && (
          <Typography
            sx={{
              ...TEXT.meta,
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {breakdown}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** 黒塗り購入の明細の1行（購入1件）。クレジットは税別なので、税別と消費税を並べる。 */
const PurchaseRow: React.FC<{ purchase: ReassistBillingPurchase }> = ({
  purchase,
}) => (
  <Box
    component="li"
    sx={(theme) => ({
      ...costItemSx(theme),
      flexWrap: "wrap",
      alignItems: "flex-start",
      columnGap: 2,
      rowGap: 1,
      listStyle: "none",
    })}
  >
    <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
      <Typography
        sx={{
          ...TEXT.meta,
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        購入日 {formatDay(purchase.purchasedOn)}
      </Typography>
      <Typography
        sx={{ ...TEXT.body, fontWeight: 700, overflowWrap: "anywhere" }}
      >
        {purchase.buildingName !== ""
          ? purchase.buildingName
          : purchase.address}
      </Typography>
      {purchase.buildingName !== "" && purchase.address !== "" && (
        <Typography
          sx={{
            ...TEXT.meta,
            color: "text.secondary",
            overflowWrap: "anywhere",
          }}
        >
          {purchase.address}
        </Typography>
      )}
      {purchase.documentType !== "" && (
        <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
          {purchase.documentType}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        flex: "0 0 auto",
        ml: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 0.5,
        textAlign: "right",
      }}
    >
      <AmountText
        amount={purchase.credits + purchase.tax}
        isProvisional={false}
        taxIncluded
        // 依頼の明細と同じく、確定のチップは出さない（購入は常に確定）
        showChip={false}
      />
      <Typography
        sx={{
          ...TEXT.meta,
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {/* 黒塗り購入は手数料だけで、経費は無い */}
        {`手数料 ${formatAmount(purchase.credits)}・消費税 ${formatAmount(
          purchase.tax
        )}`}
      </Typography>
    </Box>
  </Box>
);

/** 月のまとめ（依頼数・書類数・確定額・暫定額・依頼の合計・黒塗り購入・手数料／経費／消費税の合計・請求合計）。 */
const MonthSummary: React.FC<{
  billing: ReassistMonthlyBilling;
  provisional: string;
  total: string;
  purchase: string;
}> = ({ billing, provisional, total, purchase }) => {
  const totals = billingTotals(billing);
  const items: Array<[string, string]> = [
    ["依頼数", `${billing.requestCount}件`],
    ["書類数", `${billing.documentCount}件`],
    ["確定額（税込）", formatAmount(billing.confirmedTotal)],
    ["暫定額（税込）", provisional],
    ["依頼の合計（税込）", total],
    ["黒塗り購入（税込）", purchase],
    ["手数料の合計（税抜）", formatAmount(totals.handlingFee)],
    ["経費の合計（非課税）", formatAmount(totals.expense)],
    ["消費税の合計", formatAmount(totals.tax)],
    ["請求合計（税込）", formatAmount(billing.grandTotal)],
  ];
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        mb: 2,
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        columnGap: 2,
        rowGap: 0.5,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {items.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography
            component="dt"
            sx={{ ...TEXT.meta, color: "text.secondary" }}
          >
            {label}
          </Typography>
          <Typography
            component="dd"
            sx={{
              ...TEXT.meta,
              m: 0,
              color: "text.primary",
              fontWeight: label === "請求合計（税込）" ? 700 : 400,
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * 月の請求明細。依頼の表の詳細ダイアログ（RequestTable）と同じ作り（PC 最大 640px、SP 全画面、閉じるボタンでだけ閉じる）。
 */
export const ReassistBillingItemsDialog: React.FC<Props> = ({
  billing,
  provisionalText,
  totalText,
  purchaseText,
  onClose,
}) => {
  const isMobile = useIsMobile();
  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{ sx: { width: "100%", maxWidth: 640 } }}
      // ダイアログは画面ルートの外に描画されるため、SP の文字縮小をここで掛ける
      sx={isMobile ? listCompactSx : undefined}
      open={billing !== null}
      aria-labelledby="reassist-billing-items-title"
      // 誤タップで閉じないよう、閉じるボタン以外（背景タップ・Esc）では閉じない
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      {billing !== null && (
        <>
          <DialogTitle id="reassist-billing-items-title">
            {formatMonth(billing.month)}の請求明細
          </DialogTitle>
          <DialogContent dividers>
            <MonthSummary
              billing={billing}
              provisional={provisionalText(billing)}
              total={totalText(billing)}
              purchase={purchaseText(billing)}
            />
            {billing.items.length > 0 && (
              <Stack
                component="ul"
                spacing={1}
                aria-label={`${formatMonth(billing.month)}の請求明細`}
                sx={{ m: 0, p: 0 }}
              >
                {billing.items.map((item) => (
                  <BillingItemRow key={item.requestId} item={item} />
                ))}
              </Stack>
            )}
            {billing.purchases.length > 0 && (
              <>
                <Typography
                  component="h3"
                  sx={{ ...TEXT.body, fontWeight: 700, mt: 3, mb: 1 }}
                >
                  黒塗り資料の購入
                </Typography>
                <Stack
                  component="ul"
                  spacing={1}
                  aria-label={`${formatMonth(billing.month)}の黒塗り資料の購入`}
                  sx={{ m: 0, p: 0 }}
                >
                  {/* 同じ会社の複数人が同じ日に同じファイルを買うことがあるため、key に並び順も含める */}
                  {billing.purchases.map((purchase, index) => (
                    <PurchaseRow
                      key={`${purchase.fileId}-${purchase.purchasedOn}-${index}`}
                      purchase={purchase}
                    />
                  ))}
                </Stack>
              </>
            )}
            <Typography
              role="note"
              sx={{
                ...TEXT.meta,
                mt: { xs: 2, [MOBILE_BREAKPOINT]: 3 },
                color: "text.secondary",
              }}
            >
              依頼の請求は、納品日の月に計上します。納品前の依頼は、依頼日の月に暫定で入り、納品されると納品日の月へ移ります。黒塗り資料の購入は、購入日の月に計上します（購入額は税別で、消費税を加えた額を合計に含めます）。
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              className="TapExpanded"
              size="small"
              startIcon={<FileDownloadOutlined />}
              sx={tapExpandedSx}
              onClick={() => {
                downloadBillingCsv(billing);
              }}
            >
              CSVでダウンロード
            </Button>
            <Button
              className="TapExpanded"
              size="small"
              sx={tapExpandedSx}
              onClick={onClose}
            >
              閉じる
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
